import {useEffect, useRef, useState} from "react";

const UseSubtitles = ({subtitlesContainer, subtitles, currentTime, itemId}) => {
    const [currentSubtitle, setCurrentSubtitle] = useState(null);
    const [openApiSubtitles, setOpenApiSubtitles] = useState([]);
    const [lastSubtitleTime, setLastSubtitleTime] = useState(0);
    const subtitlesText = useRef([]);
    const currentTextIndex = useRef([]);
    const subtitlesOrder = ["spa", "esp", "es", "eng", "other"];
    const lastSubtitlesFetched = useRef(null);
    const lastAbortController = useRef(null);

    useEffect(() => {
        if (!subtitles || !subtitles.length || !itemId) return;
        const savedSubtitle = localStorage.getItem(`squadflix-subtitle-${itemId}`);
        if (savedSubtitle) {
            try {
                const subtitleObj = JSON.parse(savedSubtitle);
                const allSubtitles = [...subtitles, ...openApiSubtitles];
                const matchedSubtitle = allSubtitles.find(sub => sub.lang === subtitleObj.lang && sub.title === subtitleObj.title);
                if (matchedSubtitle) {
                    handleSetCurrentSubtitle(matchedSubtitle);
                } else {
                    const spanishSubtitle = allSubtitles.find(sub => sub.lang === subtitleObj.lang);
                    if (spanishSubtitle) {
                        handleSetCurrentSubtitle(spanishSubtitle);
                    }
                }
            } catch (e) {
                console.error("Error parsing saved subtitle:", e);
            }
        }
    }, [subtitles, itemId]);

    const getSubtitleIndex = (lang) => {
        const index = subtitlesOrder.indexOf(lang);
        return index === -1 ? subtitlesOrder.length : index;
    }

    const parseTime = (time) => {
        const [hours, minutes, seconds] = time.split(":").map(parseFloat);
        let secondsValue = hours * 3600 + minutes * 60 + seconds;

        const milliseconds = time.split(".")[1];

        if (milliseconds) {
            secondsValue += parseFloat(milliseconds) / 1000;
        }

        return parseInt(secondsValue.toFixed(4));
    }

    const parseSRT = (srtContent) => {
        const regex = /(\d+)\n(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})\n([\s\S]*?)(?=\n\n|\n?$)/g;
        const subtitles = [];
        let match;
        while ((match = regex.exec(srtContent)) !== null) {
            subtitles.push({
                start: parseTime(match[2]),
                end: parseTime(match[3]),
                text: match[4].trim(),
            });
        }
        return subtitles;
    };

    const parseVTT = (vttContent) => {
        const regex = /(\d{2}:\d{2}:\d{2}.\d{3}) --> (\d{2}:\d{2}:\d{2}.\d{3})\n([\s\S]*?)(?=\n\n|\n?$)/g;
        const subtitles = [];
        let match;
        while ((match = regex.exec(vttContent)) !== null) {
            subtitles.push({
                start: parseTime(match[1]),
                end: parseTime(match[2]),
                text: match[3].trim(),
            });
        }
        return subtitles;
    };

    const fetchSubtitleData = async (url) => {
        if (!url || url === lastSubtitlesFetched.current) {
            return;
        }

        // Aborta cualquier fetch previo
        if (lastAbortController.current) {
            lastAbortController.current.abort();
        }

        const controller = new AbortController();
        lastAbortController.current = controller;

        lastSubtitlesFetched.current = url;

        try {
            const response = await fetch(url, {signal: controller.signal});
            if (!response.ok) {
                throw new Error(`Error al recuperar los datos: ${response.status} ${response.statusText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let done = false;

            subtitlesText.current = [];
            setLastSubtitleTime(0);

            while (!done) {
                const {value, done: readerDone} = await reader.read();
                done = readerDone;

                if (controller.signal.aborted) {
                    console.log("Subtitle fetch aborted via AbortController");
                    return;
                }

                if (value) {
                    const chunkSubtitles = decoder.decode(value, {stream: !done});
                    subtitlesText.current.push(...parseSRT(chunkSubtitles));
                }

                const lastSubtitleTime = subtitlesText.current.length
                    ? subtitlesText.current[subtitlesText.current.length - 1].end
                    : 0;
                setLastSubtitleTime(lastSubtitleTime);
            }

            setLastSubtitleTime(-1);
        } catch (e) {
            if (e.name === "AbortError") {
                console.log("Fetch abortado correctamente");
                return;
            }
            if (lastSubtitlesFetched.current === url) {
                lastSubtitlesFetched.current = null;
            }
            console.error("Error al leer subtítulos:", e);
        }
    };

    const handleSetCurrentSubtitle = async (subtitle) => {

        if (!subtitle) {
            currentTextIndex.current = [];
            subtitlesText.current = [];
            setCurrentSubtitle(null);
            subtitlesContainer.current.innerHTML = "";
            localStorage.removeItem(`squadflix-subtitle-${itemId}`);
            lastSubtitlesFetched.current = null;
            return;
        }

        const subtitleSelected = {
            lang: subtitle.lang,
            title: subtitle.title,
        }

        localStorage.setItem(`squadflix-subtitle-${itemId}`, JSON.stringify(subtitleSelected));

        fetchSubtitleData(subtitle.url);

        setCurrentSubtitle(subtitle);

    };

    useEffect(() => {
        if (!subtitles || !subtitles.length || !currentSubtitle) {
            return;
        }

        const matchingTexts = subtitlesText.current.filter(subtitle => currentTime >= subtitle.start && currentTime <= subtitle.end);

        // if all matching texts are the same as currentTextIndex, do nothing
        if (matchingTexts.length === currentTextIndex.current.length && matchingTexts.every((val, index) => val.text === (currentTextIndex.current[index]?.text || ""))) {
            return;
        }

        currentTextIndex.current = matchingTexts;

        let subtitlesHtml = "";

        matchingTexts.forEach(text => {
            const tagMatch = text.text.match(/\{\\an(\d)\}/);
            let position = "";
            if (tagMatch) {
                position = `subtitle-pos-${tagMatch[1]}`;
            }

            // Eliminar la etiqueta del texto antes de mostrarlo
            const cleanText = text.text.replace(/\{\\an\d\}/g, "");

            subtitlesHtml += `<div class="subtitles-text ${position}">${cleanText.replaceAll("\n", "<br/>")}</div>`;
        });


        subtitlesContainer.current.innerHTML = subtitlesHtml;

    }, [currentSubtitle, currentTime, subtitles, subtitlesContainer]);


    return {
        lastSubtitleTime,
        currentSubtitle,
        setCurrentSubtitle: handleSetCurrentSubtitle,
        availableSubtitles: [...subtitles, ...openApiSubtitles].sort((a, b) => {
            const indexA = getSubtitleIndex(a.lang ?? "other");
            const indexB = getSubtitleIndex(b.lang ?? "other");
            return indexA - indexB;
        })
    };
}

export default UseSubtitles;
